import type { Directive } from 'vue'

// v-edit — click-to-edit any text element (the ad-builder's inline editing).
// Bind a callback; the element becomes contenteditable and, on blur, the
// callback receives the element's text so the caller can write it back to its
// own reactive state and persist. Pass a falsy value to leave the element
// read-only (e.g. `v-edit="editable && (t => ...)"`).
//
// Cross-cutting UI concern → directive (project convention), so AdCard doesn't
// repeat contenteditable/blur boilerplate on ~40 fields. The `.ab-edit`
// affordance (dashed-on-hover, moss ring on focus) lives in styles/_ab-edit.scss.
//
// Note the class is `ab-edit`, not `ad-…`: ad-block cosmetic filters hide
// elements whose class starts with "ad", which is what silently blanked this
// page before the rename. Keep edit-affordance class names ad-free.

type EditCb = (text: string) => void
type Binding = EditCb | false | null | undefined

const HANDLER = Symbol('ab-edit-handler')

interface Editable extends HTMLElement {
  [HANDLER]?: (e: FocusEvent) => void
}

function teardown(el: Editable): void {
  if (el[HANDLER]) {
    el.removeEventListener('blur', el[HANDLER])
    el[HANDLER] = undefined
  }
}

function setup(el: Editable, cb: Binding): void {
  teardown(el)
  if (typeof cb !== 'function') {
    el.removeAttribute('contenteditable')
    el.removeAttribute('spellcheck')
    el.classList.remove('ab-edit')
    return
  }
  el.setAttribute('contenteditable', 'plaintext-only')
  el.setAttribute('spellcheck', 'false')
  el.classList.add('ab-edit')
  const handler = (): void => cb(el.innerText)
  el.addEventListener('blur', handler)
  el[HANDLER] = handler
}

export const vEdit: Directive<Editable, Binding> = {
  mounted(el, binding) {
    setup(el, binding.value)
  },
  updated(el, binding) {
    // Re-bind so the closure captures the current callback (it closes over the
    // loop index / block reference, which can change across renders).
    setup(el, binding.value)
  },
  unmounted(el) {
    teardown(el)
  },
}
